export const DEFAULT_INPUT = {
  targetCorpus: 2000000,
  targetPeriod: 5,
  returnRate: 12,
  sipPeriod: 5,
  stepUp: 12,
  lumpsumAmount: 50000,
  inflation: 12,
  isInflation: true
};

export const INPUT_LIMITS = {
  targetCorpus: {min: 100000, max: 20000000, step: 100000},
  targetPeriod: {min: 1, max: 60, step: 1},
  returnRate: {min: 1, max: 12, step: 0.5},
  sipPeriod: {min: 1, max: 50, step: 1},
  stepUp: {min: 0, max: 100, step: 0.5},
  lumpsumAmount: {min: 10000, max: 2000000, step: 10000},
  inflation: {min: 1, max: 20, step: 0.5}
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const roundToStep = (value, step) => Math.round(value / step) * step;

const normalizeInput = (input) => ({
  targetCorpus: clamp(
    roundToStep(input.targetCorpus, INPUT_LIMITS.targetCorpus.step),
    INPUT_LIMITS.targetCorpus.min,
    INPUT_LIMITS.targetCorpus.max
  ),
  targetPeriod: clamp(
    Math.round(input.targetPeriod),
    INPUT_LIMITS.targetPeriod.min,
    INPUT_LIMITS.targetPeriod.max
  ),
  returnRate: clamp(
    roundToStep(input.returnRate, INPUT_LIMITS.returnRate.step),
    INPUT_LIMITS.returnRate.min,
    INPUT_LIMITS.returnRate.max
  ),
  sipPeriod: clamp(
    Math.round(input.sipPeriod),
    INPUT_LIMITS.sipPeriod.min,
    INPUT_LIMITS.sipPeriod.max
  ),
  stepUp: clamp(
    roundToStep(input.stepUp, INPUT_LIMITS.stepUp.step),
    INPUT_LIMITS.stepUp.min,
    INPUT_LIMITS.stepUp.max
  ),
  lumpsumAmount: clamp(
    roundToStep(input.lumpsumAmount, INPUT_LIMITS.lumpsumAmount.step),
    INPUT_LIMITS.lumpsumAmount.min,
    INPUT_LIMITS.lumpsumAmount.max
  ),
  inflation: clamp(
    roundToStep(input.inflation, INPUT_LIMITS.inflation.step),
    INPUT_LIMITS.inflation.min,
    INPUT_LIMITS.inflation.max
  ),
  isInflation: Boolean(input.isInflation)
});

export const calculateSipLumpsumPlan = (input) => {
  const {
    targetCorpus,
    targetPeriod,
    returnRate,
    sipPeriod,
    stepUp,
    lumpsumAmount,
    inflation,
    isInflation
  } = normalizeInput(input);

  const years = targetPeriod;
  const lumpsum = lumpsumAmount;
  const annualStepUp = stepUp;
  const inflationRate = isInflation ? inflation : 0;
  const adjustedTarget = targetCorpus * Math.pow(1 + inflationRate / 100, years);
  const monthlyRate = Math.pow(1 + returnRate / 100, 1 / 12) - 1;
  const stepUpFactor = 1 + annualStepUp / 100;

  const fvStepUpSIP = (monthlySip) => {
    let futureValue = 0;

    for (let yearIndex = 0; yearIndex < years; yearIndex += 1) {
      const isSipYear = yearIndex < sipPeriod;
      const sipThisYear = isSipYear
        ? monthlySip * Math.pow(stepUpFactor, yearIndex)
        : 0;

      for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
        futureValue += sipThisYear;
        futureValue *= 1 + monthlyRate;
      }
    }

    return futureValue;
  };

  const fvLumpsum = () => lumpsum * Math.pow(1 + returnRate / 100, years);

  let low = 0;
  let high = adjustedTarget;
  let initialSip = 0;

  for (let iteration = 0; iteration < 30; iteration += 1) {
    const mid = (low + high) / 2;
    const sipFutureValue = fvStepUpSIP(mid);
    const lumpsumFutureValue = fvLumpsum();

    if (sipFutureValue + lumpsumFutureValue < adjustedTarget) {
      low = mid;
    } else {
      high = mid;
    }

    initialSip = mid;
  }

  const returnData = [];
  let sipInvested = 0;
  let sipTotal = 0;
  let sipGain = 0;

  for (let yearIndex = 0; yearIndex < years; yearIndex += 1) {
    const isSipYear = yearIndex < sipPeriod;
    const sipThisYear = isSipYear
      ? initialSip * Math.pow(stepUpFactor, yearIndex)
      : 0;

    for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
      sipTotal += sipThisYear;
      sipTotal *= Math.pow(1 + returnRate / 100, 1 / 12);
    }

    sipInvested += sipThisYear * 12;
    sipGain = sipTotal - sipInvested;

    const lumpsumTotal = lumpsum * Math.pow(1 + returnRate / 100, yearIndex + 1);
    const lumpsumGain = lumpsumTotal - lumpsum;
    const totalInvested = sipInvested + lumpsum;
    const totalGain = sipGain + lumpsumGain;
    const totalAmount = sipTotal + lumpsumTotal;

    returnData.push({
      year: yearIndex + 1,
      sipInvested: Math.round(sipInvested),
      sipGain: Math.round(sipGain),
      sipTotal: Math.round(sipTotal),
      lumpsumInvested: Math.round(lumpsum),
      lumpsumGain: Math.round(lumpsumGain),
      lumpsumTotal: Math.round(lumpsumTotal),
      totalInvested: Math.round(totalInvested),
      totalGain: Math.round(totalGain),
      totalAmount: Math.round(totalAmount)
    });
  }

  const finalRow = returnData[returnData.length - 1] || {
    totalInvested: lumpsum,
    totalGain: 0,
    totalAmount: lumpsum
  };

  const totalAmount = finalRow.totalAmount;
  const gainPercent =
    totalAmount === 0 ? 0 : (finalRow.totalGain / totalAmount) * 100;
  const investedPercent =
    totalAmount === 0 ? 0 : (finalRow.totalInvested / totalAmount) * 100;

  return {
    initialSip: Math.round(initialSip),
    adjustedTarget: Math.round(adjustedTarget),
    returnData,
    totalInvested: finalRow.totalInvested,
    totalGain: finalRow.totalGain,
    gainPercent,
    investedPercent
  };
};

export const formatCurrency = (value) =>
  `₹${Math.round(value).toLocaleString("en-IN")}`;

export const formatPercent = (value, fractionDigits = 2) =>
  `${value.toFixed(fractionDigits)}%`;

export const snapToStep = (value, step) => roundToStep(value, step);
